import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { SnackBarService } from '@shared/snack-bar/services/snack-bar.service';
import { ModuleLoadingErrorDialogComponent } from '@root/app/module-loading-error-dialog/module-loading-error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ErrorAggregatorService } from '../../error-aggregation/error-aggregator.service';
import { HttpErrorMessageHandlerService } from '@core/error/http-error-message-handler.service';

@Injectable({ providedIn: 'root' })
export class GlobalErrorHandler implements ErrorHandler {
  private snackBarService = inject(SnackBarService);
  private readonly dialog = inject(MatDialog);
  private injector = inject(Injector);
  private handlingError = false;
  private lastErrorKey: string | null = null;
  private readonly httpErrorMessageHandlerService = inject(
    HttpErrorMessageHandlerService
  );
  handleError(error: Error | HttpErrorResponse) {
    const errorAggregatorService = this.injector.get(ErrorAggregatorService);

    // prevent recursive loops
    if (this.handlingError) {
      console.error('Recursive error detected', error);
      return;
    }
    this.handlingError = true;
    try {
      if (
        (error instanceof HttpErrorResponse &&
          error.error instanceof ProgressEvent) ||
        !navigator.onLine
      ) {
        return;
      }
      if (error instanceof HttpErrorResponse) {
        if (error.url?.includes('AttachedFiles')) {
          return;
        }

        this.httpErrorMessageHandlerService.handleFallbackError(error);
      } else if (
        error instanceof Error &&
        error.message.indexOf('Failed to fetch dynamically imported module') >
          -1
      ) {
        errorAggregatorService.addModuleError(error);
        this.dialog.open(ModuleLoadingErrorDialogComponent, {
          width: '350px',
        });
      } else {
        // Client Error
        const errorMessage = !!error.message ? error.message : error.toString();
        //ignore expression error in dev mode
        if (
          !error.message.includes('ExpressionChangedAfterItHasBeenCheckedError')
        ) {
          const errorKey = errorMessage + error?.stack;

          // prevent duplicate error, showing snackbar triggers change detection which trigger rending the template then cause the error again which causes infinite error loop
          if (this.lastErrorKey === errorKey) {
            return;
          }

          this.lastErrorKey = errorKey;
          errorAggregatorService.addConsole(error);
          console.error(error);
          setTimeout(() =>
            this.snackBarService.error(['Frontend Error', errorMessage])
          );
        }
      }
    } finally {
      this.handlingError = false;
    }
  }
}
